import React, { Fragment, useState, useEffect } from 'react';
import { Row, Col, Input, Button } from 'reactstrap';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder, ApiRequest, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { Copy } from 'react-feather';
import { handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import CreatableMultiSelect from '../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';

/**
 * GlobalSettingsSection — Manage per-job global settings (dmt_job_setting table).
 *
 * Settings are loaded into args['global'] during record processing,
 * accessible via {{global/key}} placeholders in mapping actions.
 */

const TYPE_OPTIONS = [
    { label: 'String', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Array', value: 'array' },
    { label: 'JSON', value: 'json' },
];

const detectType = (val) => {
    if (val === null || val === undefined) return 'string';
    if (Array.isArray(val)) return 'array';
    if (typeof val === 'number') return 'number';
    if (typeof val === 'object') return 'json';
    // Check if string is parseable
    if (typeof val === 'string') {
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) return 'array';
            if (typeof parsed === 'number') return 'number';
            if (typeof parsed === 'object' && parsed !== null) return 'json';
        } catch { /* keep as string */ }
    }
    return 'string';
};

const typeBadge = (type) => {
    const colors = { string: '#6c757d', number: '#0d6efd', array: '#198754', json: '#fd7e14' };
    return <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: colors[type] || '#999', marginLeft: 4 }}>{type}</span>;
};

const parseStoredValue = (val) => {
    if (val === null || val === undefined) return val;
    if (typeof val === 'string') {
        try { return JSON.parse(val); } catch { return val; }
    }
    return val;
};

const formatDisplayValue = (val) => {
    const parsed = parseStoredValue(val);
    if (parsed === null || parsed === undefined) return '';
    if (Array.isArray(parsed)) return parsed.join(', ');
    if (typeof parsed === 'object') return JSON.stringify(parsed);
    return String(parsed);
};

const GlobalSettingsSection = ({ jobData, isAdmin, isArchived, trigger }) => {

    const { confirm, ConfirmModal } = useConfirmModal();

    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(false);

    // New setting form
    const [newKey, setNewKey] = useState('');
    const [newType, setNewType] = useState('string');
    const [newValue, setNewValue] = useState('');
    const [newArrayValue, setNewArrayValue] = useState([]);
    const [newDescription, setNewDescription] = useState('');

    // Edit state
    const [editingId, setEditingId] = useState(null);
    const [editKey, setEditKey] = useState('');
    const [editType, setEditType] = useState('string');
    const [editValue, setEditValue] = useState('');
    const [editArrayValue, setEditArrayValue] = useState([]);
    const [editDescription, setEditDescription] = useState('');

    const jobId = jobData?.id || jobData?.job_id;

    const fetchSettings = () => {
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/globalSettings', {
            pathParams: { jobId },
            queryParams: { table: 'dmt_job_setting', conditions: '`type` = "setting"' },
        }).then((res) => {
            if (res.success && Array.isArray(res.data)) {
                setSettings(res.data);
            } else {
                setSettings([]);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (jobId) fetchSettings();
    }, [jobId]);

    const buildValueForSave = (type, stringVal, arrayVal) => {
        switch (type) {
            case 'array':
                return JSON.stringify(arrayVal);
            case 'number': {
                const num = Number(stringVal);
                return isNaN(num) ? JSON.stringify(stringVal) : JSON.stringify(num);
            }
            case 'json':
                try { JSON.parse(stringVal); return stringVal; }
                catch { return JSON.stringify(stringVal); }
            default:
                return JSON.stringify(stringVal);
        }
    };

    const handleCreate = () => {
        const key = newKey.trim();
        if (!key) return ToastBuilder('error', 'Key is required');

        if (settings.some(s => s.key === key)) {
            return ToastBuilder('error', `A setting with key "${key}" already exists`);
        }

        const valueToStore = buildValueForSave(newType, newValue, newArrayValue);

        ApiRequest('POST', '/dmt/jobs/{jobId}/globalSettings', {
            pathParams: { jobId },
            body: {
                job_id: parseInt(jobId),
                key,
                value: valueToStore,
                type: 'setting',
                description: newDescription.trim() || null,
            },
            queryParams: { table: 'dmt_job_setting' },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', `Setting "${key}" created`);
                setNewKey('');
                setNewValue('');
                setNewArrayValue([]);
                setNewDescription('');
                setNewType('string');
                fetchSettings();
            } else {
                ToastBuilder('error', 'Failed to create setting');
            }
        });
    };

    const handleUpdate = (settingId, originalKey) => {
        const newKey = editKey.trim();
        if (!newKey) return ToastBuilder('error', 'Key is required');

        // Check for duplicate key if renamed
        if (newKey !== originalKey && settings.some(s => s.key === newKey && s.setting_id !== settingId)) {
            return ToastBuilder('error', `A setting with key "${newKey}" already exists`);
        }

        const valueToStore = buildValueForSave(editType, editValue, editArrayValue);
        const body = [
            { op: 'replace', path: 'value', value: valueToStore },
            { op: 'replace', path: 'description', value: editDescription.trim() || null },
        ];
        if (newKey !== originalKey) {
            body.push({ op: 'replace', path: 'key', value: newKey });
        }

        ApiRequest('PATCH', '/dmt/jobs/{jobId}/globalSettings/{settingId}', {
            pathParams: { jobId, settingId },
            body,
            queryParams: { table: 'dmt_job_setting' },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', `Setting "${newKey}" updated`);
                setEditingId(null);
                fetchSettings();
            } else {
                ToastBuilder('error', 'Failed to update setting');
            }
        });
    };

    const handleDelete = async (settingId, key) => {
        const confirmed = await confirm(`Delete setting "${key}"? This cannot be undone.`, { confirmLabel: 'Delete', title: 'Delete Setting' });
        if (!confirmed) return;
        ApiRequest('DELETE', '/dmt/jobs/{jobId}/globalSettings/{settingId}', {
            pathParams: { jobId, settingId },
            queryParams: { table: 'dmt_job_setting', hardDelete: true },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', `Setting "${key}" deleted`);
                fetchSettings();
            } else {
                ToastBuilder('error', 'Failed to delete setting');
            }
        });
    };

    const startEdit = (setting) => {
        const parsed = parseStoredValue(setting.value);
        const type = detectType(parsed);
        setEditingId(setting.setting_id);
        setEditKey(setting.key);
        setEditType(type);
        setEditDescription(setting.description || '');

        if (type === 'array') {
            setEditArrayValue(Array.isArray(parsed) ? parsed : []);
            setEditValue('');
        } else if (type === 'json') {
            setEditValue(JSON.stringify(parsed, null, 2));
            setEditArrayValue([]);
        } else {
            setEditValue(String(parsed ?? ''));
            setEditArrayValue([]);
        }
    };

    const renderValueInput = (type, stringVal, setStringVal, arrayVal, setArrayVal, placeholder = '') => {
        switch (type) {
            case 'array':
                return <div className='input-sm'><CreatableMultiSelect value={arrayVal} onChange={setArrayVal} placeholder='Type and press Enter...' size='sm' /></div>;
            case 'json':
                return <div className='input-sm'><Input bsSize='sm' type='textarea' value={stringVal} onChange={(e) => setStringVal(e.target.value)}
                    placeholder='{"key": "value"}' style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 60 }} /></div>;
            case 'number':
                return <div className='input-sm'><Input bsSize='sm' type='number' value={stringVal} onChange={(e) => setStringVal(e.target.value)}
                    placeholder={placeholder || '0'} style={{ fontFamily: 'monospace' }} /></div>;
            default:
                return <div className='input-sm'><Input bsSize='sm' value={stringVal} onChange={(e) => setStringVal(e.target.value)}
                    placeholder={placeholder || 'Value'} style={{ fontFamily: 'monospace' }} /></div>;
        }
    };

    const [modal, setModal] = useState(false);

    const openModal = () => {
        fetchSettings();
        setModal(true);
    };

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Settings loaded as <code>args['global']</code> during processing. Use <code>{'{{global/key}}'}</code> in mapping placeholders.
            </p>

            {/* Existing settings */}
            {loading ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</p>
            ) : settings.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>No global settings configured for this job.</p>
            ) : (
                <div style={{ marginBottom: 16 }}>
                    {settings.map((setting) => {
                        const parsed = parseStoredValue(setting.value);
                        const type = detectType(parsed);

                        if (editingId === setting.setting_id) {
                            return (
                                <div key={setting.setting_id} style={{ padding: '10px 12px', marginBottom: 8, background: 'rgba(128,128,128,0.04)', borderRadius: 4, border: '1px solid rgba(128,128,128,0.15)' }}>
                                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                                        <Input bsSize='sm' value={editKey} onChange={(e) => setEditKey(e.target.value)}
                                            style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, maxWidth: '40%' }} />
                                        <div className='d-flex gap-1'>
                                            {TYPE_OPTIONS.map(t => (
                                                <Button key={t.value} size='sm' color={editType === t.value ? 'primary' : 'light'}
                                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                                    onClick={() => {
                                                        setEditType(t.value);
                                                        if (t.value === 'array' && editArrayValue.length === 0 && editValue) {
                                                            try {
                                                                const p = JSON.parse(editValue);
                                                                if (Array.isArray(p)) setEditArrayValue(p);
                                                            } catch { /* ignore */ }
                                                        }
                                                    }}>{t.label}</Button>
                                            ))}
                                        </div>
                                    </div>
                                    {renderValueInput(editType, editValue, setEditValue, editArrayValue, setEditArrayValue)}
                                    <div className='d-flex justify-content-between align-items-center input-sm' style={{ marginTop: 8 }}>
                                        <Input bsSize='sm' type='textarea' value={editDescription} onChange={(e) => setEditDescription(e.target.value)}
                                            placeholder='Description (optional)' style={{ maxWidth: '60%', fontSize: 11, minHeight: 40 }} />
                                        <div className='d-flex gap-1'>
                                            <Button size='sm' color='success' onClick={() => handleUpdate(setting.setting_id, setting.key)}>Save</Button>
                                            <Button size='sm' color='secondary' onClick={() => setEditingId(null)}>Cancel</Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={setting.setting_id} style={{ padding: '8px 12px', borderBottom: '1px solid rgba(128,128,128,0.1)' }}
                                className='d-flex justify-content-between align-items-center'>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className='d-flex align-items-center' style={{ gap: 6 }}>
                                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>{setting.key}</span>
                                        {typeBadge(type)}
                                        <Copy size={10} style={{ cursor: 'pointer', opacity: 0.3 }} onClick={() => handleCopy(`{{global/${setting.key}}}`)} />
                                    </div>
                                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#333', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {formatDisplayValue(setting.value)}
                                    </div>
                                    {setting.description && <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{setting.description}</div>}
                                </div>
                                {!isArchived && (
                                    <div className='d-flex gap-1' style={{ flexShrink: 0 }}>
                                        <Button size='sm' color='link' className='p-0 txt-warning' style={{ fontSize: 11 }} onClick={() => startEdit(setting)}>Edit</Button>
                                        <Button size='sm' color='link' className='p-0 text-danger' style={{ fontSize: 11 }} onClick={() => handleDelete(setting.setting_id, setting.key)}>Del</Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add new setting */}
            {!isArchived && (
                <div style={{ borderTop: '1px solid rgba(128,128,128,0.2)', paddingTop: 12 }}>
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>Add Setting</span>
                        <div className='d-flex gap-1'>
                            {TYPE_OPTIONS.map(t => (
                                <Button key={t.value} size='sm' color={newType === t.value ? 'primary' : 'light'}
                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                    onClick={() => setNewType(t.value)}>{t.label}</Button>
                            ))}
                        </div>
                    </div>
                    <Row className='align-items-start g-2 input-sm'>
                        <Col md={3}>
                            <label style={{ fontSize: 10, fontWeight: 600 }}>Key</label>
                            <Input bsSize='sm' placeholder='e.g. excluded_statuses' value={newKey}
                                onChange={(e) => setNewKey(e.target.value)} style={{ fontFamily: 'monospace' }} />
                        </Col>
                        <Col md={5}>
                            <label style={{ fontSize: 10, fontWeight: 600 }}>Value</label>
                            {renderValueInput(newType, newValue, setNewValue, newArrayValue, setNewArrayValue, 'Type value...')}
                        </Col>
                        <Col md={3}>
                            <label style={{ fontSize: 10, fontWeight: 600 }}>Description</label>
                            <Input bsSize='sm' type='textarea' placeholder='Optional' value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)} style={{ minHeight: 40 }} />
                        </Col>
                        <Col md={1} className='d-flex align-items-end' style={{ paddingTop: 16 }}>
                            <ButtonBuilder form='' label='+' className='btn-primary btn-sm px-3 py-1' style={{ fontSize: 18 }}
                                onClick={handleCreate} useSpinner={false} />
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='Global Settings' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
                <ConfirmModal />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <ModalButtonBuilder
                title='Global Settings'
                className='btn-outline-primary'
                onSubmit={false}
                onCancelLabel='Close'
                size='lg'
                onClick={fetchSettings}
                body={modalBody}
            />
            <ConfirmModal />
        </Fragment>
    );
};

export default GlobalSettingsSection;
